import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const CartContext = createContext(null);

const STORAGE_KEY = 'sdf_cart_v1';

function loadCart() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
}
function saveCart(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

export function CartProvider({ children }) {
    const [items, setItems] = useState(loadCart());
    const [previewOpen, setPreviewOpen] = useState(false);
    // Monotonic counter that increments each time the user "Confirms" the cart
    // preview. Any component (e.g. Choose Flavour popup) can watch this value
    // and close itself when it changes.
    const [confirmSeq, setConfirmSeq] = useState(0);

    useEffect(() => { saveCart(items); }, [items]);

    const openPreview = useCallback(() => setPreviewOpen(true), []);
    const closePreview = useCallback(() => setPreviewOpen(false), []);
    const confirmPreview = useCallback(() => {
        setPreviewOpen(false);
        setConfirmSeq(n => n + 1);
    }, []);

    const addItem = useCallback((item) => {
        setItems(prev => {
            const key = `${item.product_id}|${item.variant_id}|${item.flavour_id}`;
            const idx = prev.findIndex(p => `${p.product_id}|${p.variant_id}|${p.flavour_id}` === key);
            if (idx >= 0) {
                const next = [...prev];
                next[idx] = { ...next[idx], quantity: next[idx].quantity + (item.quantity || 1) };
                return next;
            }
            return [...prev, { ...item, quantity: item.quantity || 1 }];
        });
    }, []);

    const setQty = useCallback((product_id, variant_id, flavour_id, qty) => {
        setItems(prev => prev.map(p => (p.product_id === product_id && p.variant_id === variant_id && p.flavour_id === flavour_id) ? { ...p, quantity: Math.max(0, qty) } : p).filter(p => p.quantity > 0));
    }, []);

    const removeItem = useCallback((product_id, variant_id, flavour_id) => {
        setItems(prev => prev.filter(p => !(p.product_id === product_id && p.variant_id === variant_id && p.flavour_id === flavour_id)));
    }, []);

    const clear = useCallback(() => setItems([]), []);

    const { count, total } = useMemo(() => ({
        count: items.reduce((a, b) => a + b.quantity, 0),
        total: items.reduce((a, b) => a + b.price * b.quantity, 0),
    }), [items]);

    return (
        <CartContext.Provider value={{ items, count, total, addItem, setQty, removeItem, clear, previewOpen, openPreview, closePreview, confirmPreview, confirmSeq }}>
            {children}
        </CartContext.Provider>
    );
}
export const useCart = () => useContext(CartContext);
